import {useEffect, useState} from 'react'
import {View} from 'react-native'
import {Trans} from '@lingui/macro'

import {RICE_SERVICE} from '#/lib/constants'
import {
  type AllNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {logger} from '#/logger'
import {type SessionAccount, useSession, useSessionApi} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

// rice (the Semi↔PDS bridge) mints a PDS session server-side and redirects the
// browser here with a one-time ticket. We redeem it once for the session
// tokens, install them, and land the user logged in.
const RICE_SESSION_ENDPOINT = `${RICE_SERVICE}/session`

// Tickets are single-use server-side; a second fetch for the same ticket
// (remount, effect re-run) would 404 and flash the error state even though
// the first redemption succeeded. Module-level so it survives remounts.
const redeemedTickets = new Set<string>()

type Props = NativeStackScreenProps<AllNavigatorParams, 'SemiCallback'>

type Phase = 'redeeming' | 'error'

const SemiCallback = ({navigation, route}: Props) => {
  const t = useTheme()
  const {hasSession} = useSession()
  const {resumeSession} = useSessionApi()
  const [phase, setPhase] = useState<Phase>('redeeming')

  const ticket = route.params?.ticket

  // If we somehow arrive here already logged in, just go home.
  useEffect(() => {
    if (hasSession) {
      navigation.replace('Home')
    }
  }, [hasSession, navigation])

  useEffect(() => {
    let cancelled = false

    async function run() {
      if (!ticket) {
        setPhase('error')
        return
      }
      if (redeemedTickets.has(ticket)) {
        return
      }
      redeemedTickets.add(ticket)
      try {
        const res = await fetch(
          `${RICE_SESSION_ENDPOINT}/${encodeURIComponent(ticket)}`,
          {headers: {accept: 'application/json'}},
        )
        if (!res.ok) {
          throw new Error(`ticket redeem failed: ${res.status}`)
        }
        const data = await res.json()
        if (
          !data?.service ||
          !data?.did ||
          !data?.handle ||
          !data?.accessJwt ||
          !data?.refreshJwt
        ) {
          throw new Error('ticket redeem returned an incomplete session')
        }

        const account: SessionAccount = {
          service: data.service,
          did: data.did,
          handle: data.handle,
          accessJwt: data.accessJwt,
          refreshJwt: data.refreshJwt,
          // DAO backend token minted by rice ("Bearer <jwt>") — required for
          // DAO features (任务/商品/发帖); its absence must not block login.
          daoJwt: typeof data.daoJwt === 'string' ? data.daoJwt : undefined,
          // rice 自己的 API 令牌。C 端搬到 rice 之后，/api/*（个人档案、提案、
          // 评论、稻米）认的是这个，不是上面两个 —— 少了它 Semi 用户能登录，
          // 但每个业务页面都是 401。和 daoJwt 一样：拿不到也不该挡住登录。
          riceToken:
            typeof data.riceToken === 'string' ? data.riceToken : undefined,
          active: true,
        }

        await resumeSession(account)
        // resumeSession flips hasSession; the effect above redirects Home.
      } catch (err) {
        if (cancelled) return
        logger.error('SemiCallback: failed to redeem session ticket', {
          safeMessage: err instanceof Error ? err.message : String(err),
        })
        setPhase('error')
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [ticket, resumeSession])

  return (
    <View
      style={[
        a.flex_1,
        a.justify_center,
        a.align_center,
        a.gap_lg,
        a.px_xl,
        t.atoms.bg,
      ]}>
      {phase === 'redeeming' ? (
        <>
          <Loader size="xl" />
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>正在完成登录…</Trans>
          </Text>
        </>
      ) : (
        <>
          <Text style={[a.text_lg, a.font_bold, a.text_center]}>
            <Trans>登录未能完成</Trans>
          </Text>
          <Text
            style={[a.text_md, a.text_center, t.atoms.text_contrast_medium]}>
            <Trans>登录链接已失效或已被使用。请返回重新使用 Semi 登录。</Trans>
          </Text>
          <Text
            accessibilityRole="link"
            onPress={() => {
              window.location.href = `${RICE_SERVICE}/login`
            }}
            style={[a.text_md, a.font_bold, {color: t.palette.primary_500}]}>
            <Trans>重新登录</Trans>
          </Text>
        </>
      )}
    </View>
  )
}

export default SemiCallback
